import {config} from '../config';
import {getRequestContext} from './ssrHelper.ts';
import {Profile} from '../types/api.ts';
import {addSourceTracker, TrackingSource} from './tracking.ts';

export function getLocation(): Location {
    return getRequestContext().location;
}

export function getLocationPrefix(): string {
    const {protocol, host} = getLocation();
    return `${protocol}//${host}`;
}

const baseUrl = getLocationPrefix();

export function getCurrentPath(): string {
    const l = getLocation();

    return l.href.substring(l.origin.length);
}

export function getCurrentPathWithoutUtm(): string {
    const url = new URL(getLocation().toString());

    for (const key of Array.from(url.searchParams.keys())) {
        if (key.startsWith('utm_')) {
            url.searchParams.delete(key);
        }
    }

    return url.href.substring(url.origin.length);
}

export const customCustomerSubDomain: string | undefined =
    baseUrl === config.profileUrl || baseUrl === config.dashboardUrl
        ? undefined
        : getLocation().host.replace(/^([^.]+)\..+$/, '$1');

export function getOrganizationDashboardUrlAffixes(): [string, string] {
    const [scheme, host = ''] = config.dashboardUrl.split('//dashboard.', 2);

    return [`${scheme}//`, `.${host.replace(/\/$/, '')}`];
}

export const topLevelDomain = getLocation().host.replace(
    /^[^.]+\.(.+?)(:\d+)?$/,
    '$1',
);

export function isDashboardUrl(url: string): boolean {
    if (url.startsWith(config.dashboardUrl)) {
        return true;
    }

    if (customCustomerSubDomain) {
        if (url.startsWith(getLocationPrefix())) {
            const u = new URL(url);
            if (!u.pathname.startsWith('/p/')) {
                return true;
            }
        }
    }

    return false;
}

export function isProfileUrl(url: string) {
    if (url.startsWith(config.profileUrl)) {
        return true;
    }

    if (customCustomerSubDomain) {
        if (url.startsWith(getLocationPrefix())) {
            const u = new URL(url);
            if (u.pathname.startsWith('/p/')) {
                return true;
            }
        }
    }

    return false;
}

export function isValidUrl(url: string): boolean {
    try {
        new URL(url);
        return true;
    } catch (_e) {
        return false;
    }
}

export function createShareUrl(
    profile: Profile,
    source: TrackingSource,
    withCustomDomain: boolean = false,
): string {
    return addSourceTracker(
        withCustomDomain && profile.customShareUrl
            ? profile.customShareUrl
            : profile.shareUrl,
        source,
    );
}

export function isExternalUrl(url: string): boolean {
    let parsed: URL;
    try {
        parsed = new URL(url, getLocation().href);
    } catch (_e) {
        return false;
    }

    if (parsed.protocol !== 'https:') {
        return false;
    }

    return parsed.origin !== getLocation().origin;
}

const navigableProtocols = ['https:', 'mailto:'];

export function safeNavigableUrl(url: string): string | null {
    let parsed: URL;
    try {
        parsed = new URL(url, getLocation().href);
    } catch (_e) {
        return null;
    }

    return navigableProtocols.includes(parsed.protocol) ? url : null;
}

export function openUrl(url: string): void {
    const safeUrl = safeNavigableUrl(url);
    if (null === safeUrl) {
        return;
    }

    if (isExternalUrl(safeUrl)) {
        window.open(safeUrl, '_blank', 'noopener,noreferrer');

        return;
    }

    window.location.assign(safeUrl);
}
